<template>
  <div class="kanban-board">
    <b-overlay :show="loading">
      <!-- Header -->
      <b-row class="mb-2 align-items-center">
        <b-col
          cols="12"
          md="5"
        >
          <div class="d-flex align-items-start">
            <back-with-title
              v-if="board || milestone"
              :title="milestone ? milestone.name : board?.name"
              :description="milestone ? board?.name : board?.project?.name"
              :route-name="milestone ? 'project-board-milestones' : 'project-management'"
            >
              <template #append>
                <b-badge
                  v-if="board && !milestone"
                  v-b-tooltip.hover="'View Performance Report'"
                  variant="light-primary"
                  class="cursor-pointer"
                  style="margin-left: 8px;"
                  pill
                  @click="$router.push({ name: 'project-board-member-report', params: { boardUid: base58Converter(board?.uuid) } })"
                >
                  <feather-icon
                    icon="BarChart2Icon"
                  />
                </b-badge>
              </template>
            </back-with-title>
          </div>
        </b-col>
        <b-col
          v-if="board"
          cols="12"
          md="7"
          class="text-right"
        >
          <div class="d-flex align-items-center justify-content-end board-header-actions">
            <!-- Search -->
            <b-input-group class="flex-grow-1 mr-2">
              <template #prepend>
                <b-input-group-text>
                  <feather-icon
                    icon="SearchIcon"
                    size="16"
                  />
                </b-input-group-text>
              </template>

              <b-form-input
                v-model="searchQuery"
                :placeholder="$t('Search tasks')"
                autocomplete="off"
                debounce="500"
              />

              <template #append>
                <b-input-group-text
                  v-if="searchQuery"
                  class="cursor-pointer"
                  @click="searchQuery = ''"
                >
                  <feather-icon
                    icon="XIcon"
                    size="14"
                  />
                </b-input-group-text>
              </template>
            </b-input-group>

            <!-- Filter -->
            <filter-dropdown :include-filters="[onlyMe ? '' : 'assignees', 'labels', 'taskPriority']" />

            <!-- Members avatars preview -->
            <user-avatar-group
              :style="{
                width: board?.members?.data?.length < 4 ? board?.members?.data?.length * 68 + 'px' : '220px',
                left: board?.members?.data?.length < 4 ? board?.members?.data?.length * 3 + 'px' : '0'
              }"
              class="position-relative"
              :users="board?.members?.data || []"
              :loading="loading"
            />

            <!-- Quick Actions -->
            <div class="action-buttons d-flex align-items-center">
              <!-- Manage Permissions -->
              <manage-permissions
                v-if="canManagePermissions"
                :permissions="permissions"
                class="mr-1"
              />

              <!-- More actions dropdown -->
              <b-dropdown
                variant="link"
                toggle-class="p-0 board-actions-dropdown"
                :disabled="loading"
                no-caret
                right
              >
                <template #button-content>
                  <div class="action-menu-btn">
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="20"
                    />
                  </div>
                </template>
                <b-dropdown-item @click="openTaskPanel(statuses[0]?.uuid, true)">
                  <feather-icon
                    icon="PlusIcon"
                    size="16"
                    class="mr-2"
                  />
                  {{ $t('Add New Task') }}
                </b-dropdown-item>

                <div v-if="canManageBoards">
                  <b-dropdown-item @click="showMembersModal = true">
                    <feather-icon
                      icon="UsersIcon"
                      size="16"
                      class="mr-2"
                    />
                    {{ $t('Manage Members') }}
                  </b-dropdown-item>
                  <b-dropdown-item @click="showStatusManager = true">
                    <feather-icon
                      icon="SlidersIcon"
                      size="16"
                      class="mr-2"
                    />
                    {{ $t('Manage Statuses') }}
                  </b-dropdown-item>
                  <b-dropdown-item v-b-modal.manage-labels-modal>
                    <feather-icon
                      icon="TagIcon"
                      size="16"
                      class="mr-2"
                    />
                    {{ $t('Manage Labels') }}
                  </b-dropdown-item>
                  <b-dropdown-divider />
                </div>

                <div v-if="!$route.params.milestoneUid">
                  <b-dropdown-item
                    @click="$router.push({ name: 'project-board-milestones', params: { boardUid: base58Converter(board.uuid) } })"
                  >
                    <feather-icon
                      icon="CrosshairIcon"
                      size="16"
                      class="mr-2"
                    />
                    {{ $t('Milestones') }}
                  </b-dropdown-item>
                  <b-dropdown-divider />
                </div>

                <b-dropdown-item @click="$router.push({ name: 'project-management' })">
                  <feather-icon
                    icon="GridIcon"
                    size="16"
                    class="mr-2"
                  />
                  {{ $t('All Boards') }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Active Filters -->
      <active-filters />

      <!-- Tabs -->
      <b-tabs pills>
        <b-tab @click="() => { onlyMe = false; getBoard() }">
          <template #title>
            <feather-icon
              icon="ColumnsIcon"
              size="16"
              class="mr-1"
            />
            {{ $t('Backlog') }}
          </template>

          <backlog-view
            v-if="statuses"
            :statuses="statuses"
            @edit="task => { isEditingFromTaskCard = true; editTask(task) }"
            @open="viewTask"
            @enter-edit-mode="editTask"
            @refresh="loadBoard"
            @add-task="addTaskToStatus"
          />
        </b-tab>

        <b-tab @click="getMyTasks">
          <template #title>
            <feather-icon
              icon="UserIcon"
              size="16"
              class="mr-1"
            />
            {{ $t('My Tasks') }}
          </template>

          <backlog-view
            v-if="statuses"
            :statuses="statuses"
            :only-me="onlyMe"
            @open="viewTask"
            @edit="editTask"
            @refresh="loadBoard"
          />
        </b-tab>

        <b-tab lazy>
          <template #title>
            <feather-icon
              icon="UsersIcon"
              size="16"
              class="mr-1"
            />
            {{ $t('Assignees') }}
          </template>

          <assignee-view
            v-if="statuses"
            :statuses="statuses"
            @open="viewTask"
            @edit="editTask"
            @refresh="loadBoard"
            @add-task="addTaskToStatus"
          />
        </b-tab>

        <b-tab
          v-if="statuses.find(s => s.key === 'review')"
          lazy
        >
          <template #title>
            <feather-icon
              icon="PocketIcon"
              size="16"
              class="mr-1"
            />
            {{ $t('In Review') }}
          </template>

          <in-review-tasks
            :tasks="statuses.find(s => s.key === 'review')?.tasks?.data || []"
            @open="viewTask"
            @edit="editTask"
            @refresh="loadBoard"
          />
        </b-tab>
      </b-tabs>

      <!-- Task Side Panel -->
      <task-side-panel
        v-if="board"
        :visible="showTaskPanel"
        :task="editingTask"
        :editing-from-task-card="isEditingFromTaskCard"
        :editing-mode="isEditingTask"
        :board="board"
        :status-id="selectedStatusId"
        @submit="submitTask"
        @close="closeTaskPanel"
      />

      <!-- Status Manager Modal -->
      <b-modal
        v-model="showStatusManager"
        :title="$t('Manage Statuses')"
        size="lg"
        hide-footer
        centered
      >
        <status-manager
          :board="board"
          @close="showStatusManager = false"
          @updated="loadBoard"
        />
      </b-modal>

      <!-- Members Manager Modal -->
      <b-modal
        v-model="showMembersModal"
        :title="$t('Manage Board Members')"
        size="lg"
        hide-footer
        centered
      >
        <board-members-manager
          :board="board"
          @close="showMembersModal = false"
          @updated="loadBoard"
        />
      </b-modal>

      <!-- Label Manager Modal -->
      <label-manager @refresh="loadBoard" />
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BBadge,
  BModal,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BFormInput,
  BInputGroup,
  BInputGroupText,
  BTabs,
  BTab,
  BOverlay,
  VBTooltip,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import UserAvatarGroup from '@/views/common/components/UserAvatarGroup.vue'
import ManagePermissions from '@/views/common/components/ManagePermissions.vue'
import EventBus from '@/event-bus'
import { checkPermission } from '@/utils/permissions'
import FilterDropdown from '@/views/common/components/Filters/FilterDropdown.vue'
import useFilters from '@/views/common/components/Filters/useFilters'
import BackWithTitle from '@/views/common/components/BackWithTitle.vue'
import ActiveFilters from '@/views/common/components/Filters/ActiveFilters.vue'
import { getUserData } from '@/auth/utils'
import TaskSidePanel from '../tasks/TaskSidePanel.vue'
import StatusManager from './StatusManager.vue'
import BoardMembersManager from './BoardMembersManager.vue'
import LabelManager from './LabelManager.vue'
import BacklogView from './components/BacklogView.vue'
import AssigneeView from './components/AssigneeView.vue'
import InReviewTasks from './components/InReviewTasks.vue'

const {
  getActiveFilters,
  setFilters,
} = useFilters()

export default {
  name: 'KanbanBoard',
  components: {
    BTabs,
    BTab,
    BRow,
    BCol,
    BFormInput,
    BInputGroup,
    BInputGroupText,
    BBadge,
    BModal,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BOverlay,
    TaskSidePanel,
    StatusManager,
    BoardMembersManager,
    UserAvatarGroup,
    ManagePermissions,
    LabelManager,
    FilterDropdown,
    BackWithTitle,
    ActiveFilters,
    BacklogView,
    AssigneeView,
    InReviewTasks,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      me: getUserData(),
      onlyMe: false,
      searchQuery: '',
      showTaskPanel: false,
      showStatusManager: false,
      showMembersModal: false,
      editingTask: null,
      selectedStatusId: null,
      isEditingTask: false,
      isEditingFromTaskCard: false,
      submitting: false,
      permissions: ['boards.manage', 'boards.edit'],
      needPermissionCheck: false,
    }
  },
  computed: {
    ...mapGetters('boards', ['GET_CURRENT_BOARD', 'GET_CURRENT_MILESTONE_BOARD', 'GET_FETCHING_BOARD', 'GET_LOADING']),
    ...mapGetters('permissions', ['canManageBoards', 'canEditBoards', 'canManagePermissions']),
    board() {
      return this.GET_CURRENT_MILESTONE_BOARD ?? this.GET_CURRENT_BOARD
    },
    loading() {
      return this.GET_FETCHING_BOARD || this.GET_LOADING
    },
    statuses() {
      return (this.board?.statuses || []).sort((a, b) => a.position - b.position)
    },
    members() {
      return this.board?.members?.data || []
    },
    labels() {
      return this.board?.labels?.data || []
    },
    activeFilters() {
      return getActiveFilters.value
    },
    milestone() {
      const { milestoneUid } = this.$route.params

      if (!milestoneUid) return null
      return this.GET_CURRENT_BOARD?.milestones?.data.find(m => m.uuid === this.base58Converter(milestoneUid, false)) || null
    },
    taskSearch() {
      return {
        query: this.searchQuery,
        includeComments: true,
      }
    },
  },
  watch: {
    '$route.params.boardUid': {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadBoard()
        }
      },
    },
    '$route.params.taskUid': {
      immediate: true,
      handler(val) {
        if (val) {
          this.viewTask({ uuid: this.base58Converter(val, false) })
        }
      },
    },
    members(value) {
      if (value) {
        setFilters('assignees', [
          { uuid: 'unassigned', name: this.$t('Unassigned'), icon: 'UserIcon' },
          ...value,
        ])
      }
    },
    labels(value) {
      if (value) {
        setFilters('labels', value)
      }
    },
    activeFilters: {
      handler() {
        if (this.board) this.getBoard()
      },
      deep: true,
    },
    searchQuery() {
      if (this.board) this.getBoard()
    },
  },
  mounted() {
    EventBus.$on('permission-updated', permission => {
      if (this.permissions.indexOf(permission) !== -1) {
        this.needPermissionCheck = true
        setTimeout(() => this.checkUserPermission(), 2000)
      }
    })
    this.checkUserPermission()
  },
  beforeDestroy() {
    EventBus.$off('permission-updated')
  },
  methods: {
    ...mapActions('boards', ['fetchBoard', 'fetchTask', 'moveTask', 'createTask', 'updateTask', 'fetchTasksByStatusKey']),
    ...mapActions('permissions', ['setCanManageBoards', 'setCanEditBoards']),
    checkUserPermission() {
      if (this.needPermissionCheck || !this.canManageBoards || !this.canEditBoards) {
        checkPermission(this.permissions, permission => {
          const canManageBoards = permission['boards.manage'] === true
          const canEditBoards = permission['boards.edit'] === true
          this.setCanManageBoards(canManageBoards)
          this.setCanEditBoards(canEditBoards)
          this.needPermissionCheck = false
        })
      }
    },
    async loadBoard() {
      try {
        await this.fetchBoard({
          uuid: this.base58Converter(this.$route.params.boardUid, false),
          milestoneUid: this.$route.params.milestoneUid ? this.base58Converter(this.$route.params.milestoneUid, false) : null,
        })
      } catch (error) {
        this.showError(error.message)
      }
    },
    getTasksForStatus(statusId) {
      return this.tasks.filter(t => t.status?.id === statusId)
    },
    addTaskToStatus(status) {
      this.selectedStatusId = status.id
      this.editingTask = null
      this.isEditingTask = true
      this.showTaskPanel = true
    },
    openTaskPanel(statusId = null, isCreatingTask = false) {
      this.selectedStatusId = statusId
      this.editingTask = null
      this.isEditingTask = isCreatingTask
      this.showTaskPanel = true
    },
    async viewTask(task) {
      this.editingTask = await this.fetchTask(task.uuid)
      this.selectedStatusId = task.status?.id
      this.isEditingTask = false
      this.showTaskPanel = true
    },
    async editTask(task) {
      this.editingTask = await this.fetchTask(task.uuid)
      this.selectedStatusId = task.status?.id
      this.isEditingTask = true
      this.showTaskPanel = true
    },
    closeTaskPanel() {
      this.showTaskPanel = false
      this.isEditingTask = false
      this.isEditingFromTaskCard = false
      this.editingTask = null
      this.selectedStatusId = null
    },
    async submitTask(taskData) {
      this.submitting = true
      try {
        const payload = {
          ...taskData,
          projectUid: taskData.projectUid || this.board?.project?.uuid,
        }

        if (this.editingTask) {
          const result = await this.updateTask({
            uuid: this.editingTask.uuid,
            input: payload,
          })
          this.showSuccess(result.message)
        } else {
          const { projectUid, ...data } = payload
          const result = await this.createTask({
            boardUid: this.board.uuid,
            projectUid,
            input: data,
          })
          this.showSuccess(result.message)
        }
        this.closeTaskPanel()
        this.loadBoard()
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.submitting = false
      }
    },
    getMyTasks() {
      this.onlyMe = true
      this.getBoard()
    },
    getBoard() {
      const assignees = this.onlyMe ? [this.me.uuid] : this.activeFilters?.assignees.map(item => item.uuid) || []
      const labelUids = this.activeFilters?.labels?.map(item => item.uuid) || []
      const statusKeys = this.activeFilters?.statuses?.map(item => item.key) || []
      const priority = this.activeFilters?.taskPriority

      this.fetchBoard({
        uuid: this.board?.uuid,
        assignees,
        labelUids,
        statusKeys,
        priority,
        search: this.taskSearch,
        milestoneUid: this.$route.params.milestoneUid ? this.base58Converter(this.$route.params.milestoneUid, false) : null,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/base/pages/kanban-board.scss";
</style>
